<script lang="ts" setup>
import { computed, onMounted, PropType, Ref, ref, WritableComputedRef } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    VueFlowNodeModel,
    SelectOptionsServiceGetConnectFlowsResult,
} from '@/types';
import store from '@/store';
import { ExternalLinkIcon } from '@heroicons/vue/outline';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { required } from '@/utils/validators';
import { useI18n } from 'vue-i18n';
import { SelectOptionsService } from '@/services';

const props = defineProps({
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
});

const showForm = ref(false);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

const { t } = useI18n();

interface GoToFormState {
    connectFlow: number | null;
}
const formState: Ref<GoToFormState> = ref({
    connectFlow: null,
});
const rules = computed<ValidationArgs<GoToFormState>>(() => {
    const r: ValidationArgs<GoToFormState> = {
        connectFlow: {
            required,
        },
    };
    return r;
});
const v$: Ref<Validation<ValidationArgs<GoToFormState>>> = useVuelidate(rules, formState);

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}

async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value) {
        nodeWritable.value.data.goToConnectFlow = formState.value.connectFlow || undefined;
        showForm.value = false;
    }
    nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    v$.value.$reset();
}

const connectFlowOptions: Ref<SelectOptionsServiceGetConnectFlowsResult[]> = ref([]);

const nodeText = computed(() => {
    let text = t('connectFlow.node.goToConnectFlow.description');
    if (nodeWritable.value.data.goToConnectFlow) {
        const connectFlow = connectFlowOptions.value.find(
            (e) => e.id == nodeWritable.value.data.goToConnectFlow,
        );
        text = connectFlow?.name ?? '';
    }
    return text;
});

onMounted(async () => {
    if (nodeWritable.value.data.goToConnectFlow !== undefined) {
        formState.value.connectFlow = nodeWritable.value.data.goToConnectFlow;
    }

    const { data } = await SelectOptionsService.getConnectFlows();
    connectFlowOptions.value = data;

    v$.value.$validate().then((result) => {
        isFormCorrect.value = result;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    });
});
</script>

<template>
    <UiNode
        :nodeId="node.id"
        :title="$t('connectFlow.node.goToConnectFlow.title')"
        :icon="ExternalLinkIcon"
        showActions
        @editNode="showForm = true"
        @removeNode="removeNode"
        :class="isFormCorrect ? '' : 'border-red-300 shadow-red-300'"
    >
        <div class="font-medium text-gray-600">
            <span>
                {{ nodeText }}
            </span>
        </div>
    </UiNode>

    <UiRightPanel
        v-model="showForm"
        :title="$t('connectFlow.node.goToConnectFlow.title')"
        :icon="ExternalLinkIcon"
    >
        <form autocomplete="off" class="flex flex-col gap-3" @submit.prevent="onSubmit">
            <div class="sm:col-span-12">
                <label class="mb-2 block text-sm font-medium text-gray-800">
                    {{ $t('connectFlow.node.goToConnectFlow.helpText') }}
                </label>
                <UiMultiselect
                    name="schedule"
                    v-model="v$.connectFlow.$model"
                    :options="connectFlowOptions"
                    :errors="v$.connectFlow.$errors"
                    label="name"
                    value-prop="id"
                    primitive
                    required
                />
            </div>

            <div class="mt-4 grid grid-cols-2 place-content-around gap-4 text-center">
                <UiButton variant="white" textVariant="gray-700" @click="showForm = false">
                    {{ $t('core.actions.Cancel') }}
                </UiButton>
                <UiButton type="submit" variant="primary" textVariant="white">
                    {{ $t('core.actions.Apply') }}
                </UiButton>
            </div>
        </form>
    </UiRightPanel>
</template>
